import LocationFeature from '../models/feature';
import SearchThisArea from '../components/search-this-area';
import DrawYourSearch from '../components/draw-your-search';
import { locationTypes } from '../../locations';
import { getFeatureLabel, isFeatureClickable } from '../features';

import {
    Coast,
    District,
    Entity,
    Island,
    Municipality,
    Neighbourhood,
    Province,
    Region,
} from '../../locations';

const allowedSources = [
    Region,
    Province,
    Coast,
    Island,
    Municipality,
    District,
    Neighbourhood,
    Entity,
    'properties',
    'radius-close',
    'area-close',
];

const defaultOptions = {
    labelFunc: getFeatureLabel,
};

function allowMouse( ctx, features ) {
    if ( !features ) {
        return false;
    }
    if ( ctx.activeComponents.has( DrawYourSearch.id ) ) {
        return false;
    }
    if ( !allowedSources.includes( features[ 0 ].source ) ) {
        return false;
    }
    return true;
}

function onPropertyLayerEnter( ctx, event ) {
    const features = ctx.map.queryRenderedFeatures( event.point );

    const allowed = allowMouse( ctx, features );

    if ( !allowed ) {
        return;
    }

    onLocationLayerLeave( ctx, event );

    const feature = features[ 0 ];

    ctx.currentCursorTarget = feature;

    ctx.map.getCanvas().style.cursor = 'pointer';

    ctx.properties.forEach( ( property ) => {
        if ( property.id === feature.properties.id ) {
            property.hover = true;
            return;
        }
        property.hover = false;
    } );

    if ( !ctx.map.getLayer( 'properties' ) ) {
        return;
    }

    let propertyLayout = [ 'case' ];

    ctx.properties.forEach( ( property ) => {
        propertyLayout.push(
            [
                'boolean',
                [ '==', [ 'get', 'id' ], property.id ],
                false,
            ],
            property.getIcon(),
        );
    } );

    propertyLayout.push( [ 'get', 'icon' ] );

    if ( ctx.map.getLayer( 'properties' ) ) {
        ctx.map.setLayoutProperty( 'properties', 'icon-image',
            propertyLayout,
            {
                validate: ctx.debug,
            },
        );
    }
}

function onPropertyLayerLeave( ctx, event ) {
    ctx.map.getCanvas().style.cursor = '';

    ctx.properties.forEach( ( property ) => {
        property.hover = false;
    } );

    if ( !ctx.map.getLayer( 'properties' ) ) {
        return;
    }

    let propertyLayout = [ 'case' ];

    ctx.properties.forEach( ( property ) => {
        propertyLayout.push(
            [
                'boolean',
                [ '==', [ 'get', 'id' ], property.id ],
                false,
            ],
            property.getIcon(),
        );
    } );

    propertyLayout.push( [ 'get', 'icon' ] );

    if ( ctx.map.getLayer( 'properties' ) ) {
        ctx.map.setLayoutProperty( 'properties', 'icon-image',
            propertyLayout,
            {
                validate: ctx.debug,
            },
        );
    }
}

function onLocationLayerEnter( ctx, event, options = defaultOptions ) {
    if ( ctx.activeComponents.has( SearchThisArea.id ) ) {
        return;
    }

    const features = ctx.map.queryRenderedFeatures( event.point );

    const allowed = allowMouse( ctx, features );

    if ( !allowed ) {
        return;
    }

    if ( features.findIndex( ( feature ) => locationTypes.includes( feature.source ) ) !== 0 ) {
        return;
    }

    const feature = features[ 0 ];

    if ( !feature.id ) {
        return;
    }

    if ( ctx.currentCursorTarget && ctx.currentCursorTarget.layer.id === 'properties' ) {
        onPropertyLayerLeave( ctx, event );
    }

    options = Object.assign( {}, defaultOptions, options );

    const previouSearch = new URLSearchParams( ctx.previousSearchState );

    if ( previouSearch.has( 'area' ) && !ctx.activeComponents.has( SearchThisArea.id ) ) {
        return;
    }

    if ( !ctx.map.getSource( feature.source ) ) {
        return;
    }

    const locationFeature = LocationFeature.fromMapBoxFeature( feature );

    const isSameAsCurrentFeature = ( ctx.currentLocation && ctx.currentCursorTarget && parseInt( ctx.currentLocation.id ) === locationFeature.id );

    if ( isSameAsCurrentFeature ) {
        ctx.map.setFeatureState(
            {
                source: ctx.currentCursorTarget.source,
                id: ctx.currentCursorTarget.id.toString(),
            },
            {
                hover: false,
            },
        );
    }

    const clickable = isFeatureClickable( locationFeature );
    let currentSrc;

    if ( ctx.currentCursorTarget ) {
        currentSrc = ctx.currentCursorTarget.source;
    }

    if ( ctx.currentCursorTarget !== null && ctx.currentCursorTarget.id && currentSrc && ctx.map.getSource( currentSrc ) ) {
        ctx.map.setFeatureState(
            {
                source: currentSrc,
                id: ctx.currentCursorTarget.id.toString(),
            },
            {
                hover: false,
            },
        );
    }

    ctx.currentCursorTarget = feature;

    const featureSrc = feature.source;

    if ( clickable && ctx.map.getSource( featureSrc ) && !isSameAsCurrentFeature ) {
        ctx.map.setFeatureState(
            {
                source: featureSrc,
                id: feature.id,
            },
            {
                hover: true,
            },
        );
    }

    if ( !ctx.touchEnabled ) {
        const labelText = options.labelFunc( ctx.language, feature );
        ctx.locationIndicator.update( labelText );
    }

    if ( clickable && !isSameAsCurrentFeature ) {
        ctx.map.getCanvas().style.cursor = 'pointer';
    }
}

function onLocationLayerLeave( ctx, event ) {
    ctx.map.getCanvas().style.cursor = '';

    if ( ctx.currentCursorTarget !== null && ctx.currentCursorTarget.id && ctx.map.getSource( ctx.currentCursorTarget.source ) ) {
        ctx.map.setFeatureState(
            {
                source: ctx.currentCursorTarget.source,
                id: ctx.currentCursorTarget.id.toString(),
            },
            {
                hover: false,
            },
        );
    }

    ctx.locationIndicator.clear();

    ctx.currentCursorTarget = null;
}

function onEventDispatcherLayerEnter( ctx, event ) {
    const features = ctx.map.queryRenderedFeatures( event.point );

    const allowed = allowMouse( ctx, features );

    if ( !allowed ) {
        return;
    }

    onLocationLayerLeave( ctx, event );
    onPropertyLayerLeave( ctx, event );

    const feature = features[ 0 ];

    ctx.map.getCanvas().style.cursor = 'pointer';

    if ( feature.properties.label ) {
        ctx.locationIndicator.update( feature.properties.label );
    }
}

function onEventDispatcherLayerLeave( ctx, event ) {
    ctx.map.getCanvas().style.cursor = '';
    ctx.locationIndicator.clear();
}

export {
    onPropertyLayerEnter,
    onPropertyLayerLeave,
    onLocationLayerEnter,
    onLocationLayerLeave,
    onEventDispatcherLayerEnter,
    onEventDispatcherLayerLeave,
};
